/**
 * API Service Layer
 * Axios configuration and API client
 */

import axios, { AxiosInstance, AxiosRequestConfig } from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

// Endpoints that must NEVER go through the 401 refresh-and-retry logic.
function isAuthEndpoint(url: string): boolean {
  return (
    url.includes("/auth/token") ||
    url.includes("/auth/login") ||
    url.includes("/auth/registration")
  );
}

class ApiService {
  private client: AxiosInstance;
  // Single-flight refresh: concurrent 401s share ONE refresh call so a
  // rotated/blacklisted refresh token is not reused (ROTATE_REFRESH_TOKENS=True).
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const url: string = originalRequest.url || "";

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthEndpoint(url)
        ) {
          originalRequest._retry = true;
          try {
            const access = await this.refreshAccessToken();
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${access}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private refreshAccessToken(): Promise<string> {
    if (!this.refreshPromise) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        return Promise.reject(new Error("No refresh token"));
      }
      this.refreshPromise = axios
        .post(`${API_URL}/auth/token/refresh/`, { refresh: refreshToken })
        .then((response) => {
          const access = response.data.access;
          localStorage.setItem("access_token", access);
          // Rotation returns a new refresh token; persist it so the next
          // refresh does not reuse a blacklisted one.
          if (response.data.refresh) {
            localStorage.setItem("refresh_token", response.data.refresh);
          }
          return access;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise;
  }

  // Generic CRUD methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  // File upload helper
  async uploadFile<T = any>(url: string, file: File, additionalData?: Record<string, any>): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.keys(additionalData).forEach((key) => {
        formData.append(key, additionalData[key]);
      });
    }

    const response = await this.client.post<T>(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  }
}

export default new ApiService();
